import { motion } from 'framer-motion'
import { Clock, TrendingUp, ShieldAlert } from 'lucide-react'
import { SectionLabel } from '../ui/SectionLabel'

const cards = [
  {
    icon: Clock,
    title: 'IT Fires Steal Your Day',
    body: "Every hour your team spends dealing with tech issues is an hour they're not serving clients or moving your business forward. The cost is invisible — until you add it up.",
  },
  {
    icon: TrendingUp,
    title: 'Your Competitors Are Automating',
    body: 'AI tools and workflow automation are compressing what used to take hours into minutes. Companies that adopt now build advantages that compound. The gap widens every month.',
  },
  {
    icon: ShieldAlert,
    title: "Security Risks You Can't See",
    body: "For legal and medical practices, a single breach isn't just expensive — it's existential. Most SMBs are one unpatched system away from a disruption they can't recover from.",
  },
]

export function ProblemSection() {
  return (
    <section style={{ padding: '120px 0', background: 'var(--bg-2)' }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <SectionLabel className="mb-6">The Problem</SectionLabel>
          <h2
            style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(38px, 5vw, 60px)',
              lineHeight: 1.0,
              textTransform: 'uppercase',
              marginBottom: 64,
              maxWidth: 720,
              color: 'var(--white)',
            }}
          >
            Your tech shouldn't be <span style={{ color: 'var(--accent)', fontStyle: 'normal' }}>costing</span> you growth.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-[2px]">
          {cards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="problem-card relative overflow-hidden transition-colors duration-250"
              style={{
                background: 'var(--bg-3)',
                padding: '44px 36px',
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 72,
                  fontWeight: 900,
                  color: 'rgba(78,125,233,0.45)',
                  lineHeight: 1,
                  marginBottom: 16,
                }}
              >
                {String(i + 1).padStart(2, '0')}
              </div>
              <card.icon className="w-10 h-10 mb-4" style={{ color: 'var(--accent)' }} />
              <h3
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 22,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: 14,
                  color: 'var(--white)',
                }}
              >
                {card.title}
              </h3>
              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.7,
                  color: 'var(--white-dim)',
                }}
              >
                {card.body}
              </p>
              {i === 2 && (
                <>
                  <p style={{
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: 'var(--white-dim)',
                    marginTop: 20,
                    paddingTop: 16,
                    borderTop: '1px solid rgba(238,240,245,0.08)',
                  }}>
                    {'Every NextTech Consultants client with 12 or more employees receives '}
                    <strong style={{ color: 'var(--white)' }}>
                      three SPECTRA Certificates of Resilience
                    </strong>
                    {' — covering Endpoint Security, BEC Protection, and Backup — each backed by a '}
                    <strong style={{ color: 'var(--white)' }}>$10,000 warranty</strong>
                    {' — '}
                    <strong style={{ color: 'var(--white)' }}>$30,000 in total coverage</strong>
                    {'.'}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <picture>
                      <source srcSet="/badges/spectra-endpoint.webp" type="image/webp" />
                      <img
                        src="/badges/spectra-endpoint.png"
                        alt="SPECTRA Certified Endpoint Security"
                        width={32}
                        height={32}
                        style={{ height: 32, width: 32, opacity: 0.9, display: 'block' }}
                      />
                    </picture>
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .problem-card:hover { background: var(--bg-4) !important; }
        .problem-card::after {
          content: "";
          position: absolute;
          bottom: 0; left: 0;
          width: 100%; height: 2px;
          background: var(--accent);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }
        .problem-card:hover::after { transform: scaleX(1); }
      ` }} />
    </section>
  )
}
